import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { usePageTitle } from "@/hooks/use-page-title";
import { motion, MotionConfig } from "motion/react";
import {
  ArrowLeft,
  Settings,
  Bot,
  Rocket,
  Key,
  Trash2,
  AlertTriangle,
  Palette,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ApplicationHeader,
  DiscordInfoCard,
  ApplicationSettingsForm,
  BotCustomizeForm,
} from "@/components/applications";
import { ApplicationPageSkeleton } from "@/components/applications/application-skeleton";
import { DeploymentStatusCard } from "@/components/applications/deployment-status-card";
import { DeploymentLogs } from "@/components/applications/deployment-logs";
import { BotCredentialsForm } from "@/components/applications/bot-credentials-form";
import { GlobalLoader } from "@/components/ui/global-loader";
import { IconContainer } from "@/components/ui/icon-container";
import { useApplication, useDeleteBot } from "@/lib/api";
import { getErrorMessage } from "@/lib/api/errors";

export const Route = createFileRoute("/dashboard/applications/$applicationId")({
  component: ApplicationManagementPage,
});

function ApplicationManagementPage() {
  const { applicationId } = Route.useParams();
  const navigate = useNavigate();
  const {
    data: applicationData,
    isPending: applicationPending,
    error,
  } = useApplication(applicationId);
  const deleteMutation = useDeleteBot(applicationId);
  usePageTitle(applicationData?.data?.application?.name ?? "Application");

  if (deleteMutation.isPending) {
    return <GlobalLoader message="Deleting your application..." />;
  }

  if (applicationPending) {
    return <ApplicationPageSkeleton />;
  }

  if (error || !applicationData?.data?.application) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild className="gap-1.5">
          <Link to="/dashboard">
            <ArrowLeft className="size-4" />
            Back to Dashboard
          </Link>
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <IconContainer
              icon={Bot}
              size="lg"
              variant="muted"
              className="mb-3"
            />
            <p className="text-muted-foreground">
              Application not found or you don&apos;t have access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const application = applicationData.data.application;
  const isCustom = application.isCustom;

  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="space-y-6"
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            asChild
            aria-label="Back to dashboard"
          >
            <Link to="/dashboard">
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <ApplicationHeader application={application} />
        </div>

        <Tabs defaultValue={isCustom ? "deployment" : "settings"}>
          <TabsList>
            {isCustom && (
              <TabsTrigger value="deployment" className="gap-1.5">
                <Rocket className="size-4" />
                Deployment
              </TabsTrigger>
            )}
            <TabsTrigger value="settings" className="gap-1.5">
              <Settings className="size-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="customize" className="gap-1.5">
              <Palette className="size-4" />
              Customize
            </TabsTrigger>
            <TabsTrigger value="discord" className="gap-1.5">
              <Bot className="size-4" />
              Discord Info
            </TabsTrigger>
            {isCustom && (
              <TabsTrigger value="credentials" className="gap-1.5">
                <Key className="size-4" />
                Credentials
              </TabsTrigger>
            )}
          </TabsList>

          {isCustom && (
            <TabsContent value="deployment" className="space-y-6">
              <DeploymentStatusCard applicationId={applicationId} />
              <DeploymentLogs applicationId={applicationId} />
              <DeleteBotCard
                application={application}
                deleteMutation={deleteMutation}
                navigate={navigate}
              />
            </TabsContent>
          )}

          <TabsContent value="settings">
            <ApplicationSettingsForm
              applicationId={applicationId}
              application={application}
            />
          </TabsContent>

          <TabsContent value="customize">
            <BotCustomizeForm
              applicationId={applicationId}
              application={application}
            />
          </TabsContent>

          <TabsContent value="discord">
            <DiscordInfoCard
              applicationId={applicationId}
              application={application}
            />
          </TabsContent>

          {isCustom && (
            <TabsContent value="credentials">
              <BotCredentialsForm applicationId={applicationId} />
            </TabsContent>
          )}
        </Tabs>
      </motion.div>
    </MotionConfig>
  );
}

function DeleteBotCard({
  application,
  deleteMutation,
  navigate,
}: {
  application: { name: string };
  deleteMutation: ReturnType<typeof useDeleteBot>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [confirmName, setConfirmName] = useState("");
  const [open, setOpen] = useState(false);

  const canDelete = confirmName === application.name;

  return (
    <Card className="border-destructive/30">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="bg-destructive/10 flex size-10 items-center justify-center rounded-xl">
            <AlertTriangle className="text-destructive size-5" />
          </div>
          <div>
            <CardTitle>Danger Zone</CardTitle>
            <CardDescription>Irreversible actions</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="border-destructive/20 flex items-center justify-between rounded-lg border p-4">
          <div>
            <p className="text-sm font-medium">Delete this bot</p>
            <p className="text-muted-foreground text-sm">
              Permanently delete this bot, its deployment, and all associated
              data including tickets, transcripts, documents, and guild
              settings.
            </p>
          </div>
          <AlertDialog
            open={open}
            onOpenChange={(v) => {
              setOpen(v);
              if (!v) setConfirmName("");
            }}
          >
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                className="shrink-0 gap-1.5"
              >
                <Trash2 className="size-4" />
                Delete Bot
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Custom Bot</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3">
                    <p>
                      This will permanently delete this bot and stop its
                      deployment. All associated data will be destroyed,
                      including:
                    </p>
                    <ul className="list-disc space-y-1 pl-4">
                      <li>Guild connections and settings</li>
                      <li>Tickets, messages, and transcripts</li>
                      <li>Knowledge base documents</li>
                      <li>User permissions and audit history</li>
                    </ul>
                    <p>This action cannot be undone.</p>
                    <div className="pt-1">
                      <p className="text-foreground mb-2">
                        To confirm, type the bot name below:
                      </p>
                      <button
                        type="button"
                        className="bg-muted/50 text-foreground hover:bg-muted mb-2 flex w-full items-center justify-between rounded-md border px-3 py-2 font-mono text-sm transition-colors"
                        onClick={() => {
                          navigator.clipboard.writeText(application.name);
                          toast.success("Copied to clipboard");
                        }}
                      >
                        <span>{application.name}</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-muted-foreground size-3.5"
                        >
                          <rect
                            width="14"
                            height="14"
                            x="8"
                            y="8"
                            rx="2"
                            ry="2"
                          />
                          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                        </svg>
                      </button>
                      <Input
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        placeholder={application.name}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <Button
                  variant="destructive"
                  disabled={!canDelete}
                  onClick={() => {
                    deleteMutation.mutate(undefined, {
                      onSuccess: () => {
                        toast.success("Bot deleted successfully");
                        navigate({ to: "/dashboard" });
                      },
                      onError: (error) => {
                        toast.error(
                          getErrorMessage(error, "Failed to delete bot")
                        );
                      },
                    });
                  }}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
